import { debounce } from "./debounce"
import styles from "./url-chooser.css?inline"
import html from "./url-chooser.html?raw"
import { sharedStyles } from "../../shared-styles"

const MIME_TYPES: Record<string, string> = {
  "application/json": "JSON",
  "application/ld+json": "JSON-LD",
  "text/turtle": "Turtle",
  "application/x-turtle": "Turtle",
  "text/plain": "plain text",
  "application/octet-stream": "binary data",
}

export interface URLCredentials {
  user: string
  pass: string
}

interface URLChangeEventDetail {
  url: URL
  credentials: URLCredentials | null
}

export interface URLChangeEvent extends CustomEvent {
  detail: URLChangeEventDetail
}

function acceptable(contentType: string | null, accept: string[]): boolean {
  if (accept.length === 0) {
    return true
  }
  if (contentType === null) {
    return false
  }
  const mimeType = contentType.split(";")[0]
  for (const acceptedType of accept) {
    if (mimeType === acceptedType) {
      return true
    }
  }
  return false
}

function showMIMEType(contentType: string | null): string {
  if (contentType == null) {
    return "no content type"
  }
  const mimeType = contentType.split(";")[0]
  if (mimeType in MIME_TYPES) {
    return MIME_TYPES[mimeType]
  } else {
    return mimeType
  }
}

function showAccept(accept: string[]): string {
  const labels = Array.from(new Set(accept.map(showMIMEType)))
  if (labels.length <= 2) return labels.join(" or ")
  return `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`
}

function showBytes(n: number): string {
  if (n < 1024) return `${n} bytes`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// Wrap fetch in an arrow function so it's resolved at call time, not module load time.
// This allows tests to mock globalThis.fetch and have the mock be used.
const debouncedFetch = debounce(
  (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
    fetch(input, init),
)

async function checkHeaders(
  url: string,
  accept: string[] = [],
  credentials: URLCredentials | null = null,
): Promise<[Response | null, string, number | null]> {
  try {
    // No Accept request header: the configured RDF mime types join into a
    // value over the 128-byte CORS-safelist limit for Accept, which would
    // turn this simple GET into a preflighted request. Strict-but-CORS-enabled
    // servers (e.g. data.perio.do) don't allow Accept in preflight, so the
    // dataset would fail to load. We validate the *response* content-type
    // against `accept` instead, so the request header isn't needed.
    const headers: Record<string, string> = {}
    if (credentials !== null) {
      headers["Authorization"] =
        "Basic " + btoa(`${credentials.user}:${credentials.pass}`)
    }
    const init: RequestInit | undefined =
      Object.keys(headers).length > 0 ? { headers } : undefined
    const response = await debouncedFetch(url, init)
    if (response.status !== 200) {
      return [
        null,
        `Request to URL failed (${response.status})`,
        response.status,
      ]
    }

    const contentType = response.headers.get("content-type")
    if (acceptable(contentType, accept)) {
      return [
        response,
        `Request to URL returned ${showMIMEType(contentType)}`,
        response.status,
      ]
    } else {
      return [
        null,
        `Request to URL did not return ${showAccept(accept)}`,
        response.status,
      ]
    }
  } catch (e) {
    return [
      null,
      `Request to URL failed: ${e instanceof Error ? e.message : String(e)}`,
      null,
    ]
  }
}

class URLChooser extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["accept", "no-preview"]
  }

  private _extraValidation:
    | ((response: Response) => Promise<[boolean, string]> | [boolean, string])
    | null = null
  private _accept: string[] = []
  private _credentials: URLCredentials | null = null
  private _maxBytes: number | null = null

  attributeChangedCallback(
    name: string,
    _oldValue: string | null,
    newValue: string | null,
  ) {
    if (name === "accept") {
      this._accept =
        newValue === null
          ? []
          : newValue
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
    } else if (name === "no-preview") {
      const preview = this.shadowRoot?.querySelector<HTMLElement>("#preview")
      if (preview) preview.style.display = newValue === null ? "" : "none"
    }
  }

  constructor() {
    super()
    const template = document.createElement("template")
    template.innerHTML = `
      <style>${styles}</style>
      ${html}
    `
    const shadow = this.attachShadow({ mode: "open" })
    shadow.adoptedStyleSheets = [sharedStyles]
    shadow.appendChild(template.content.cloneNode(true))
  }

  private initializeURLList() {
    const root = this.shadowRoot!
    const slot = root.querySelector<HTMLSlotElement>('slot[name="url-list"]')!
    const urlList = slot
      .assignedElements()
      .find((el) => el.tagName === "DATALIST") as HTMLDataListElement
    if (urlList) {
      const datalist = root.querySelector<HTMLDataListElement>("#url-list")!
      const input = root.querySelector<HTMLInputElement>("#url-input")!
      datalist.innerHTML = ""
      Array.from(urlList.options).forEach((option) => {
        datalist.appendChild(option.cloneNode(true))
      })
      input.setAttribute("list", datalist.id)
    }
  }

  get extraValidation():
    | ((response: Response) => Promise<[boolean, string]> | [boolean, string])
    | null {
    return this._extraValidation
  }

  set extraValidation(
    f: (response: Response) => Promise<[boolean, string]> | [boolean, string],
  ) {
    if (typeof f === "function") {
      this._extraValidation = f
    }
  }

  set accept(mimeTypes: string[]) {
    this._accept = mimeTypes
  }

  get accept(): string[] {
    return this._accept
  }

  set maxBytes(n: number) {
    this._maxBytes = n
  }

  get maxBytes(): number | null {
    return this._maxBytes
  }

  openWithUrl(url: string) {
    const root = this.shadowRoot!
    const dialog = root.querySelector<HTMLDialogElement>("#dialog")!
    const input = root.querySelector<HTMLInputElement>("#url-input")!
    input.value = url
    dialog.showModal()
    input.dispatchEvent(new Event("input"))
  }

  connectedCallback() {
    const root = this.shadowRoot!
    const openButton = root.querySelector<HTMLButtonElement>("#open-button")!
    const retryButton = root.querySelector<HTMLButtonElement>("#retry-button")!
    const cancelButton =
      root.querySelector<HTMLButtonElement>("#cancel-button")!
    const okButton = root.querySelector<HTMLButtonElement>("#ok-button")!
    const dialog = root.querySelector<HTMLDialogElement>("#dialog")!
    const input = root.querySelector<HTMLInputElement>("#url-input")!
    const status = root.querySelector<HTMLElement>("#status")!
    const preview = root.querySelector<HTMLElement>("#preview")!
    const slot = root.querySelector<HTMLSlotElement>('slot[name="url-list"]')!
    const authFieldset = root.querySelector<HTMLFieldSetElement>("#auth")!
    const authUser = root.querySelector<HTMLInputElement>("#auth-user")!
    const authPass = root.querySelector<HTMLInputElement>("#auth-pass")!
    const authSubmit = root.querySelector<HTMLButtonElement>("#auth-submit")!

    this.initializeURLList()
    slot.addEventListener("slotchange", () => {
      this.initializeURLList()
    })

    const setAuthState = (state: "pending" | "success" | "error" | null) => {
      authFieldset.classList.remove("pending", "success", "error")
      if (state !== null) authFieldset.classList.add(state)
    }

    const resetAuth = () => {
      this._credentials = null
      authFieldset.hidden = true
      setAuthState(null)
      authUser.value = ""
      authPass.value = ""
    }

    const validateURL = async () => {
      okButton.disabled = true
      if (input.validity.valid) {
        status.className = ""
        status.textContent = "Checking URL…"
        const [response, validationMessage, responseStatus] =
          await checkHeaders(input.value, this._accept, this._credentials)
        if (responseStatus === 401) {
          authFieldset.hidden = false
          if (this._credentials !== null) {
            setAuthState("error")
            status.className = "error"
            status.textContent = "Invalid username or password"
          } else {
            setAuthState(null)
            status.className = ""
            status.textContent = ""
          }
          retryButton.disabled = true
          return
        }
        let valid = response !== null
        let finalMessage = validationMessage
        let sizeWarning = false
        if (valid && this._maxBytes !== null) {
          const cl = response!.headers.get("content-length")
          if (cl !== null) {
            const size = parseInt(cl, 10)
            if (Number.isFinite(size) && size > this._maxBytes) {
              valid = false
              finalMessage = `Too large to load (${showBytes(size)}; max ${showBytes(this._maxBytes)})`
            }
          } else {
            sizeWarning = true
            finalMessage = `Size unknown — load may fail if larger than ${showBytes(this._maxBytes)}`
          }
        }
        if (valid && this._extraValidation) {
          ;[valid, finalMessage] = await Promise.resolve(
            this._extraValidation(response!),
          )
          if (!valid) sizeWarning = false
        }
        status.className = valid
          ? sizeWarning
            ? "warning"
            : "success"
          : "error"
        status.textContent = finalMessage
        if (valid) {
          if (this._credentials !== null) setAuthState("success")
          retryButton.disabled = true
          okButton.disabled = false
          okButton.focus()
        } else {
          retryButton.disabled = false
        }
      } else {
        status.className = "error"
        if (
          input.validity.valueMissing ||
          input.validity.typeMismatch ||
          input.validity.patternMismatch
        ) {
          status.textContent = "Please enter a full URL"
        } else {
          status.textContent = input.validationMessage
          for (const key in input.validity) {
            if (input.validity[key as keyof ValidityState]) {
              console.log(key)
            }
          }
        }
      }
    }

    openButton.addEventListener("click", () => dialog.showModal())
    retryButton.addEventListener("click", validateURL)
    input.addEventListener("focus", () => {
      if (input.value === "" && input.showPicker) {
        try {
          input.showPicker()
        } catch {
          // not allowed in some browsers
        }
      }
    })
    input.addEventListener("input", () => {
      // A URL change may target a different realm; drop stale credentials.
      this._credentials = null
      validateURL()
    })
    authSubmit.addEventListener("click", (e) => {
      e.preventDefault()
      this._credentials = { user: authUser.value, pass: authPass.value }
      setAuthState("pending")
      void validateURL()
    })
    const clearErrorOnEdit = () => {
      if (authFieldset.classList.contains("error")) {
        setAuthState(null)
        status.className = ""
        status.textContent = ""
      }
    }
    authUser.addEventListener("input", clearErrorOnEdit)
    authPass.addEventListener("input", clearErrorOnEdit)
    cancelButton.addEventListener("click", () => {
      resetAuth()
      dialog.close()
    })
    dialog.addEventListener("close", () => {
      resetAuth()
    })
    okButton.addEventListener("click", () => {
      const url = new URL(input.value)
      const credentials = this._credentials
      const event = new CustomEvent<URLChangeEventDetail>("change", {
        detail: { url, credentials },
      })
      this.dispatchEvent(event)

      preview.firstChild?.remove()
      const p = document.createElement("p")
      p.title = url.toString()
      p.innerHTML = `<span class="hostname">${url.hostname}</span><span">${url.pathname}</span>`
      preview.appendChild(p)
      dialog.close()
    })
  }
}
customElements.define("url-chooser", URLChooser)
export { URLChooser }
